import type {
  AnnounceArtist,
  AnnounceArtistInput,
  AnnounceAttachment,
  AnnounceAttachmentInput,
  CreateAnnounceDetailInput,
  Maybe,
  Mutation,
  Query, TrackingAttributes,
} from '@/generated/graphql';
import { useConnectionStore } from '@/stores/connection';
import { useRootStore } from '@/stores/root';
import { defineStore } from 'pinia';
import type { FormRequest } from 'vuniform';
import type RequestResult from '@/types/RequestResult';
import * as apolloCore from '@apollo/client/core';
import * as Sentry from '@sentry/vue';
import { Events } from '@vp/common';
import { LoadingState } from '@/util/LoadingState';

const { gql } = apolloCore;

export enum ButtonTextEnum {
  TICKETS = 'Tickets',
  FREE = 'Free',
  POSTPONED = 'Postponed',
  CANCELLED = 'Cancelled',
  CUSTOM = 'Custom',
}

export enum TicketLinkModeEnum {
  USE_VP_LINK = 'Use VP link',
  USE_EXTERNAL_LINK = 'Use external link',
  NO_TICKET_LINK = 'No ticket link',
}

export type TrackingEntry = {
  id: number;
  provider?: 'ga' | 'facebook';
  trackingId?: string;
};

export interface AnnounceDetail {
  id: number;
  event_id: number;
  promoter: string;
  event_name: string;
  support: string;
  description: string;
  website_url: string;
  facebook_url: string;
  twitter_url: string;
  instagram_url: string;
  ticketsUrl: string;
  defaultTicketsUrl: string;
  ticketsUrlMode?: string;
  images: string[];
  announceAt: string;
  highlightedImageIdx: number;
  buttonText: string;
  buttonTextAdditional?: string;
  on_sale_at: string;
  triggered_at: string;
  status: string;
  tags: string[] | string; // XXX: Temporary, until token input is fixed
  attachments: AnnounceAttachment[];
  announceArtists: AnnounceArtist[];
  tracking: TrackingEntry[];
}

export interface Event {
  id: number;
  name: string;
  date: string;
  startTime: string;
  doorTime: string;
  timezone: string;
  announceDetail?: AnnounceDetail;
}

type EventState = {
  selectedEvent?: Event;
  selectedEventLoading: boolean;
  events: Events.Event.EventType[];
  eventsLoading: LoadingState;
  page: number;
  pageSize: number;
  allEventsLoaded: boolean;
  aggregatedEventSales: Events.AggregatedEventSales.AggregatedEventSalesType;
};

const ANNOUNCE_FIELDS = gql`
  fragment AnnounceFields on AnnounceDetail {
    announceAt
    createdAt
    description
    eventId
    eventName
    facebookUrl
    highlightedImageIdx
    id
    images
    instagramUrl
    onSaleAt
    promoter
    status
    support
    tags
    ticketsUrl
    defaultTicketsUrl
    triggeredAt
    twitterUrl
    updatedAt
    websiteUrl
    artists
    buttonText
    tracking {
      id
      provider
      trackingId
    }
    attachments {
      attachment {
        id
        url
        status
      }
      highlighted
    }
    announceArtists {
      id
      name
      instagram
      facebook
      twitter
      website
      spotify
      youtube
      lastfm
      applemusic
      wikipedia
      songkick
      bandcamp
    }
  }
`;

const GET_EVENT_QUERY = gql`
  query GetEvent($eventId: Int!) {
    event(id: $eventId) {
      id
      date
      doorTime
      startTime
      endTime
      name
      timezone
      announceDetail {
        ...AnnounceFields
      }
    }
  }
  ${ANNOUNCE_FIELDS}
`;

export interface SaveAnnounceParams {
  eventId: number;
  promoter: string;
  eventName: string;
  description: string;
  announceAt: string;
  highlightedImageIdx: Maybe<number>;
  buttonText: string;
  tags: string[] | string; // XXX: Temporary, until token input is fixed
  attachments?: AnnounceAttachmentInput[];
  announceArtists?: AnnounceArtistInput[];
  tracking?: TrackingAttributes[];
}

const SAVE_ANNOUNCE_MUTATION = gql`
  mutation SaveAnnounce($announceInput: CreateAnnounceDetailInput!) {
    createAnnounceDetail(input: $announceInput) {
      announceDetail {
        ...AnnounceFields
      }
      errors
    }
  }
  ${ANNOUNCE_FIELDS}
`;

export const useEventStore = defineStore('event', {
  state: (): EventState => ({
    events: [],
    eventsLoading: LoadingState.UNKNOWN,
    page: 1,
    pageSize: 15,
    allEventsLoaded: false,
    aggregatedEventSales: Events.AggregatedEventSales.getNewAggregatedEventSales(),
    selectedEvent: undefined,
    selectedEventLoading: false,
  }),

  getters: {
    isEventsLoading(state) {
      return state.eventsLoading === LoadingState.LOADING;
    },
    totalSales(state) {
      return state.aggregatedEventSales.salesData.totalSales;
    },
    ticketsSold(state) {
      return state.aggregatedEventSales.salesData.ticketsSold;
    },
    totalTickets(state) {
      return state.aggregatedEventSales.salesData.totalTickets;
    },
    totalTicketsSalesToday(state) {
      return state.aggregatedEventSales.salesData.totalTicketsSalesToday;
    },
    totalTicketsSoldToday(state) {
      return state.aggregatedEventSales.salesData.totalTicketsSoldToday;
    },
    salesPercentage(state) {
      return Events.Utils.getSalesPercentage(state.aggregatedEventSales);
    },
    ticketsPercentage(state) {
      return Events.Utils.getTicketsPercentage(state.aggregatedEventSales);
    },
    getSelectedEventId(): number | undefined {
      const rootStore = useRootStore();
      const id = parseInt(<string>rootStore.route?.params?.eventId);
      return isNaN(id) ? undefined : id;
    },
    getSelectedEvent(state: EventState): Event | undefined {
      return (state.selectedEventLoading || undefined) && state.selectedEvent;
    },
  },

  actions: {
    async fetchEventsNextPage() {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      if (this.eventsLoading === LoadingState.LOADING || this.allEventsLoaded) {
        return;
      }

      this.eventsLoading = LoadingState.LOADING;

      try {
        const newEvents = await Events.Api.getEvents(apiClient, this.page, this.pageSize);

        if (newEvents.length < this.pageSize) {
          this.allEventsLoaded = true;
        }

        this.events = [...this.events, ...newEvents];
        this.page += 1;
        this.eventsLoading = LoadingState.DONE;
      } catch (error) {
        this.eventsLoading = LoadingState.ERROR;
        Sentry.captureException(error);
        console.error('Error fetching events:', error);
        throw error;
      }
    },

    async fetchAggregatedEventSales() {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      try {
        this.aggregatedEventSales = await Events.Api.getAggregatedEventSales(apiClient);
      } catch (error) {
        Sentry.captureException(error);
        console.error('Failed to fetch aggregated event sales:', error);
        throw error;
      }
    },

    async fetchSelectedEvent() {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      const selectedEventId = this.getSelectedEventId;

      if (this.getSelectedEvent) {
        return;
      }

      if (!selectedEventId) {
        throw 'No selected event id.';
      }

      if (this.selectedEventLoading) {
        // TODO: wait until finished loading and check if the required event is loaded
        return;
      }

      this.selectedEventLoading = true;
      try {
        const { data } = await apiClient.query<Query>({
          query: GET_EVENT_QUERY,
          variables: {
            eventId: selectedEventId,
          },
          fetchPolicy: 'network-only',
        });
        this.selectedEvent = data.event as Event;
        if (this.selectedEvent.announceDetail?.tags) {
          this.selectedEvent = {
            ...this.selectedEvent,
            announceDetail: {
              ...this.selectedEvent.announceDetail,
              tags: (this.selectedEvent.announceDetail.tags as string[]).join(','),
            },
          };
        }
        if (this.selectedEvent.announceDetail) {
          const buttonText = this?.selectedEvent?.announceDetail?.buttonText;
          const isButtonTextCustom = !(Object.keys(ButtonTextEnum) as (keyof typeof ButtonTextEnum)[]).find(
            key => ButtonTextEnum[key] === buttonText,
          );
          if (buttonText) {
            if (isButtonTextCustom) {
              this.selectedEvent.announceDetail.buttonText = ButtonTextEnum.CUSTOM;
              this.selectedEvent.announceDetail.buttonTextAdditional = buttonText;
            } else {
              this.selectedEvent.announceDetail.buttonText = buttonText;
              this.selectedEvent.announceDetail.buttonTextAdditional = undefined;
            }
          } else {
            this.selectedEvent.announceDetail.buttonText = ButtonTextEnum.TICKETS;
          }

          this.setSelectedEventTicketLinkMode();
        }
      } finally {
        this.selectedEventLoading = false;
      }
    },

    async saveAnnounceDetail(payload: FormRequest<SaveAnnounceParams>): Promise<RequestResult<unknown>> {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: SAVE_ANNOUNCE_MUTATION,
          variables: {
            announceInput: <CreateAnnounceDetailInput>{
              announceDetail: {
                ...payload.params,
                tags: (payload.params.tags as string)
                  .split(',')
                  .map(v => v.trim())
                  .filter(v => v),
              },
            },
          },
        });
        if (!data?.createAnnounceDetail?.errors) {
          this.selectedEvent = {
            ...this.selectedEvent,
            announceDetail: {
              ...(data?.createAnnounceDetail?.announceDetail ?? {}),
            },
          };
          if (this.selectedEvent?.announceDetail?.tags) {
            this.selectedEvent.announceDetail.tags = (this.selectedEvent.announceDetail.tags as string[]).join(', ');
          }

          this.setSelectedEventTicketLinkMode();
        } else {
          return { errors: data.createAnnounceDetail.errors };
        }
      } finally {
        //
      }

      return {};
    },

    setSelectedEventTicketLinkMode() {
      const announceDetail = this?.selectedEvent?.announceDetail;

      if (!announceDetail) {
        return;
      }

      const { ticketsUrl, defaultTicketsUrl } = announceDetail;

      if (!ticketsUrl) {
        announceDetail.ticketsUrlMode = TicketLinkModeEnum.NO_TICKET_LINK;
      } else if (ticketsUrl === defaultTicketsUrl) {
        announceDetail.ticketsUrlMode = TicketLinkModeEnum.USE_VP_LINK;
      } else {
        announceDetail.ticketsUrlMode = TicketLinkModeEnum.USE_EXTERNAL_LINK;
      }
    },
  },
});
